// src/pages/TenantDetails.jsx
import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_TENANT, DELETE_TENANT } from '../graphql/tenants';

const TenantDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const { loading, error, data } = useQuery(GET_TENANT, {
    variables: { id },
    fetchPolicy: 'network-only' // Force fetch from server to get latest data
  });

  const [deleteTenant] = useMutation(DELETE_TENANT, {
    onCompleted: () => navigate('/tenants'),
    onError: error => {
      console.error('Error deleting tenant:', error);
      alert('Failed to delete tenant. Please try again.');
    }
  });

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${data?.tenant?.firstName} ${data?.tenant?.lastName}? This action cannot be undone.`)) {
      deleteTenant({ variables: { id } });
    }
  };
  
  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
      <strong className="font-bold">Error!</strong>
      <span className="block sm:inline"> {error.message}</span>
    </div>
  );

  const { tenant } = data;
  const formatDate = dateString => {
    if (!dateString) return 'Not specified';
    
    try {
      // Try parsing as ISO date string first
      const date = new Date(dateString);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date format';
      }
      
      // Use toLocaleDateString with the 'en-GB' locale for DD/MM/YYYY format
      return date.toLocaleDateString('en-GB', { 
        year: 'numeric', 
        month: 'numeric', 
        day: 'numeric' 
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return 'Invalid date';
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          Tenant Details
        </h1>
        <Link to="/tenants" className="flex items-center text-blue-600 hover:text-blue-800">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Tenants
        </Link>
      </div>

      {/* Action Buttons */}
      <div className="mb-6 flex space-x-3">
        <Link 
          to={`/tenants/edit/${tenant.id}`} 
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Edit
        </Link>
        <Link 
          to={`/tenants/${tenant.id}/documents`}
          className="inline-flex items-center px-4 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Documents
        </Link>
        <button
          onClick={handleDelete}
          className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          Delete
        </button>
      </div>

      {/* Tenant Info */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div className="flex items-center">
            <div className="bg-blue-100 text-blue-700 rounded-full h-16 w-16 flex items-center justify-center text-2xl font-bold mr-4">
              {tenant.firstName.charAt(0)}{tenant.lastName.charAt(0)}
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">{tenant.firstName} {tenant.lastName}</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                {tenant.properties?.length || 0} {tenant.properties?.length === 1 ? 'Property' : 'Properties'}
              </p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Email</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <a href={`mailto:${tenant.email}`} className="text-blue-600 hover:underline">{tenant.email}</a>
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Primary Phone</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <a href={`tel:${tenant.primaryPhone}`} className="text-blue-600 hover:underline">{tenant.primaryPhone}</a>
              </dd>
            </div>
            {tenant.secondaryPhone && (
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Secondary Phone</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <a href={`tel:${tenant.secondaryPhone}`} className="text-blue-600 hover:underline">{tenant.secondaryPhone}</a>
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Documentation Status */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Documentation Status</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Current status of tenant documentation
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center">
              <div className={`h-4 w-4 rounded-full mr-3 ${tenant.hasFicaDocument ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className={`text-sm ${tenant.hasFicaDocument ? 'text-green-700' : 'text-red-700'}`}>
                FICA Documentation {tenant.hasFicaDocument ? 'Complete' : 'Pending'}
              </span>
            </div>
            <div className="flex items-center">
              <div className={`h-4 w-4 rounded-full mr-3 ${tenant.hasIdDocument ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className={`text-sm ${tenant.hasIdDocument ? 'text-green-700' : 'text-red-700'}`}>
                ID Document {tenant.hasIdDocument ? 'Complete' : 'Pending'}
              </span>
            </div>
            <div className="flex items-center">
              <div className={`h-4 w-4 rounded-full mr-3 ${tenant.bankingDetails ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className={`text-sm ${tenant.bankingDetails ? 'text-green-700' : 'text-red-700'}`}>
                Banking Details {tenant.bankingDetails ? 'Complete' : 'Pending'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Properties Section */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Properties</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Properties associated with this tenant
          </p>
        </div>
        <div className="border-t border-gray-200">
          {tenant.properties && tenant.properties.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {tenant.properties.map(property => (
                <li key={property.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                  <Link to={`/properties/${property.id}`} className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <p className="text-sm font-medium text-blue-600">{property.address?.street}</p>
                      <p className="text-sm text-gray-500">
                        {property.address?.suburb}, {property.address?.city} {property.address?.postalCode}
                      </p>
                    </div>
                    <div className="flex items-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        property.status === 'occupied' ? 'bg-green-100 text-green-800' :
                        property.status === 'vacant' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {property.status?.charAt(0).toUpperCase() + property.status?.slice(1)}
                      </span>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-5 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <p className="mt-2 text-sm text-gray-500">No properties found for this tenant.</p>
              <div className="mt-4">
                <Link 
                  to="/properties/add" 
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Property
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* System Info */}
      <div className="mt-4 text-sm text-gray-500">
        <p>Created: {formatDate(tenant.createdAt)} • Last Updated: {formatDate(tenant.updatedAt)}</p>
      </div>
    </div>
  );
};

export default TenantDetails;